import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';

import { ActionStore, EntityStore } from '@igo2/common';
import { ConfigService } from '@igo2/core';
import {
  FEATURE,
  Feature,
  FeatureMotion,
  IgoMap,
  Layer,
  LayerService,
  MapService,
  Research,
  SearchResult,
  SearchService
} from '@igo2/geo';
import { MapState, QueryState } from '@igo2/integration';

import { BehaviorSubject } from 'rxjs';

import { SearchState } from './search.state';
import { SharedDataService } from 'src/app/services/shared-data.service';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavComponent implements OnInit, OnDestroy {
  title$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

  @Input()
  get map(): IgoMap {
    return this._map;
  }

  set map(value: IgoMap) {
    this._map = value;
  }
  private _map: IgoMap;

  @Input()
  get opened(): boolean {
    return this._opened;
  }
  set opened(value: boolean) {
    if (value === this._opened) {
      return;
    }

    this._opened = value;
    this.openedChange.emit(this._opened);
  }
  private _opened: boolean;

  @Output() openedChange = new EventEmitter<boolean>();
  @Output() sidenavClosed = new EventEmitter<boolean>();

  @Input()
  get sidenavOpened(): boolean {
    return this._sidenavOpened;
  }
  set sidenavOpened(value: boolean) {
    this._sidenavOpened = value;
  }
  private _sidenavOpened: boolean;

  @Input()
  get mobile(): boolean {
    return this._mobile;
  }
  set mobile(value: boolean) {
    this._mobile = value;
  }
  private _mobile: boolean;

  //QUERY

  @Input()
  get mapQueryClick(): boolean {
    return this._mapQueryClick;
  }
  set mapQueryClick(value: boolean) {
    this._mapQueryClick = value;
  }
  private _mapQueryClick: boolean;

  get queryStore(): EntityStore<SearchResult> {
    return this.queryState.store;
  }

  resultSelected$ = new BehaviorSubject<SearchResult<Feature>>(undefined);

  // Feature details
  @Output() selectFeature = new EventEmitter<boolean>();

  @Input()
  get feature(): Feature {
    return this._feature;
  }
  set feature(value: Feature) {
    this._feature = value;
    this.cdRef.detectChanges();
    this.selectFeature.emit();
  }
  private _feature: Feature;

  public selectedFeature: Feature;
  public hasFeatureEmphasisOnSelection: Boolean = false;

  // SEARCH

  @Input()
  get searchInit(): boolean {
    return this._searchInit;
  }

  set searchInit(value: boolean) {
    this._searchInit = value;
    if (this._searchInit) {
      this.openPanelSearch();
    }
  }

  private _searchInit: boolean;

  public hasToolbox: boolean = undefined;
  public store = new ActionStore([]);
  public igoSearchPointerSummaryEnabled: boolean = false;

  public termSplitter: string = '|';

  @ViewChild('mapBrowser', { read: ElementRef, static: true })
  mapBrowser: ElementRef;

  public lonlat;
  public mapProjection: string;
  public term: string;
  public settingsChange$ = new BehaviorSubject<boolean>(undefined);

  get searchStore(): EntityStore<SearchResult> {
    return this.searchState.store;
  }

  // legend

  @Input()
  get layers(): Layer[] {
    return this._layers;
  }
  set layers(value: Layer[]) {
    this._layers = value;
  }
  private _layers: Layer[];

  @Input()
  get legendPanelOpened(): boolean {
    return this._legendPanelOpened;
  }
  set legendPanelOpened(value: boolean) {
    this._legendPanelOpened = value;

    if (this._legendPanelOpened) {
      this.openPanelLegend();
    }
  }
  private _legendPanelOpened: boolean;

  @Input()
  get scenarioDateToggle(): string {
    return this._scenarioDateToggle;
  }
  set scenarioDateToggle(value: string) {
    this._scenarioDateToggle = value;
  }
  private _scenarioDateToggle: string;

  public mapLayersShownInLegend: Layer[];

  isSmallScreen: boolean = false;

  constructor(
    private configService: ConfigService,
    private mapService: MapService,
    private layerService: LayerService,
    private searchState: SearchState,
    private searchService: SearchService,
    private queryState: QueryState,
    private cdRef: ChangeDetectorRef,
    private mapState: MapState,
    private sharedDataService: SharedDataService
  ) {
    this.hasToolbox =
      this.configService.getConfig('hasToolbox') === undefined
        ? false
        : this.configService.getConfig('hasToolbox');
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isSmallScreen = window.innerWidth <= 822;
  }

  ngOnInit() {
    this.queryStore.entities$.subscribe((entities) => {
      // eslint-disable-next-line eqeqeq
      if (entities.length == 1 && entities[0].data.properties.numero_immeuble) {
        this.opened = true;
        this.mapQueryClick = true;
        this.onClearSearch();

        if (this.hasActivePassiveContent()) {
          this.secondarySidenav.toggle();
          this.legendPanelOpened = false;
        }
        if (this.mobile) {
          this.sharedDataService.setSidenavResults(true);
        }
      } else {
        if (this.mobile) {
          this.sharedDataService.setSidenavResults(false);
        }
      }
    });

    this.onResize();
  } // End OnInit

  @HostListener('change')
  ngOnDestroy() {
    this.store.destroy();
    this.store.entities$.unsubscribe();
    this.opened = false;
    this.legendPanelOpened = false;
    this.onClearSearch();
    this.clearQuery();
    this.map.propertyChange$.unsubscribe;
  }

  //SEARCH
  onPointerSummaryStatusChange(value) {
    this.igoSearchPointerSummaryEnabled = value;
  }

  onSearchTermChange(term = '') {
    this.term = term;
    const termWithoutHashtag = term.replace(/(#[^\s]*)/g, '').trim();
    if (termWithoutHashtag.length < 2) {
      this.searchStore.clear();
      this.selectedFeature = undefined;
    }
  }

  onSearch(event: { research: Research; results: SearchResult[] }) {
    if ((this.mapQueryClick = true)) {
      // to clear the mapQuery if a search is initialized
      this.queryState.store.softClear();
      this.map.queryResultsOverlay.clear();
      this.mapQueryClick = false;
    }
    this.store.clear();
    // search
    this.searchInit = true;
    const results = event.results;
    this.searchStore.state.updateAll({ focused: false, selected: false });
    const newResults = this.searchStore.entities$.value
      .filter((result: SearchResult) => result.source !== event.research.source)
      .concat(results);
    this.searchStore.updateMany(newResults);
  }

  onSearchSettingsChange() {
    this.settingsChange$.next(true);
  }

  /**
   * Try to add a feature to the map when it's being focused
   * @internal
   * @param result A search result that could be a feature
   */
  onResultFocus(result: SearchResult) {
    this.tryAddFeatureToMap(result);
    this.selectedFeature = (result as SearchResult<Feature>).data;
  }

  /**
   * Try to add a feature to the map overlay
   * @param layer A search result that could be a feature
   */
  private tryAddFeatureToMap(layer: SearchResult) {
    if (layer.meta.dataType !== FEATURE) {
      return undefined;
    }

    // Somethimes features have no geometry. It happens with some GetFeatureInfo
    if (layer.data.geometry === undefined) {
      return;
    }

    this.map.searchResultsOverlay.setFeatures(
      [layer.data] as Feature[],
      FeatureMotion.Default
    );

    this.hasFeatureEmphasisOnSelection = this.configService.getConfig(
      'hasFeatureEmphasisOnSelection'
    );
  }

  /*
   * Remove a feature to the map overlay
   */
  removeFeatureFromMap() {
    this.map.searchResultsOverlay.clear();
  }

  onSearchCoordinate() {
    this.searchStore.clear();
    const results = this.searchService.reverseSearch(this.lonlat);

    for (const i in results) {
      if (results.length > 0) {
        results[i].request.subscribe((_results: SearchResult<Feature>[]) => {
          this.onSearch({ research: results[i], results: _results });
        });
      }
    }
  }

  closePanelOnCloseQuery(event) {
    this.opened = false;
    this.mapQueryClick = false;
  }

  onSearchPanel() {}

  onClearSearch() {
    this.map.searchResultsOverlay.clear();
    this.searchStore.clear();
    this.searchState.setSelectedResult(undefined);
    this.searchState.deactivateCustomFilterTermStrategy();
    this.searchInit = false;
    this.term = '';
    this.searchState.setSearchTerm('');
  }

  clearQuery(): void {
    this.queryState.store.softClear();
    this.queryState.store.clear();
    this.mapQueryClick = false;
    this.removeFeatureFromMap();
  }

  // LEGEND

  closePanelOnCloseLegend(event) {
    this.opened = this.hasActivePrimaryContent();
    this.legendPanelOpened = false;
  }

  openPanelLegend() {
    this.opened = true;
  }

  openPanelSearch() {
    this.opened = true;
    this.clearQuery();
    this.mapQueryClick = false;

    if (this.hasActivePassiveContent()) {
      this.secondarySidenav.toggle();
      this.legendPanelOpened = false;
    }
  }

  hasActivePassiveContent(): boolean {
    return this.legendPanelOpened === true;
  }

  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('secondarySidenav') secondarySidenav: MatSidenav;

  public toggleSidenav(): void {
    if (this.hasActivePassiveContent()) {
      this.secondarySidenav.toggle();
      if (this.hasActivePrimaryContent()) {
        this.sidenav.close();
      }
    } else {
      this.sidenav.toggle();
    }
    this.opened = this.sidenav.opened || this.secondarySidenav.opened;
  }

  hasActivePrimaryContent(): boolean {
    return (this.searchInit === true || this.mapQueryClick === true);
  }
}
